import { supabase } from '@/lib/supabase'
import type { LoginFormValues, RegisterFormValues } from '@/types'

// ─────────────────────────────────────────────────────────────
// Auth service
// All auth operations go through Supabase Auth.
// The handle_new_user DB trigger creates the profile automatically.
// ─────────────────────────────────────────────────────────────

export const authService = {

  async login({ email, password }: LoginFormValues) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw new Error(error.message)
    return data
  },

  async register({ email, password, username, full_name }: RegisterFormValues) {
    // Check username availability first (before creating auth user)
    const { data: existing } = await supabase
      .from('profiles')
      .select('id')
      .eq('username', username.toLowerCase())
      .maybeSingle()

    if (existing) {
      throw new Error('Username is already taken. Please choose another.')
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          // Passed to handle_new_user trigger via raw_user_meta_data
          full_name,
          // Override the auto-generated username with the user's chosen one
          preferred_username: username.toLowerCase(),
        },
      },
    })

    if (error) throw new Error(error.message)

    // The trigger creates the profile with an auto-generated username.
    // Update it to the user's preferred username immediately.
    if (data.user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ username: username.toLowerCase(), full_name })
        .eq('id', data.user.id)

      if (profileError) throw new Error(profileError.message)
    }

    return data
  },

  async logout() {
    const { error } = await supabase.auth.signOut()
    if (error) throw new Error(error.message)
  },

  async getSession() {
    const { data, error } = await supabase.auth.getSession()
    if (error) throw new Error(error.message)
    return data.session
  },

  async getUser() {
    const { data, error } = await supabase.auth.getUser()
    if (error) throw new Error(error.message)
    return data.user
  },

  onAuthStateChange(callback: Parameters<typeof supabase.auth.onAuthStateChange>[0]) {
    return supabase.auth.onAuthStateChange(callback)
  },

  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${import.meta.env.VITE_APP_URL}/reset-password`,
    })
    if (error) throw new Error(error.message)
  },
}
